import loadable from '@loadable/component';
import React from 'react';
import { Route, IndexRoute } from 'react-router';
import LoadingPage from './components/LoadingPage';
import { Page } from './containers/Page';
import { connectPage } from './containers/connectPage';
import {
    BASE_URL,
    CHECKOUT_PATH,
    DELIVERY_METHODS_PATH,
    EXTERNAL_PAYMENT_PATH,
    EXISTING_ORDER_PATH,
    GROUP_REG_CREATE_PATH,
    GROUP_REG_JOIN_PATH,
    GROUP_REG_TYPE_SELECTION_PATH,
    LOGIN_PATH,
    ONLY_CHECKOUT_PATH,
    PAY_IN_PATH,
    PAY_IN_CONFIRMATION_PATH,
    PICK_A_SEAT_PATH,
    PROTECTED_PATH,
    PURCHASE_CONFIRMATION_PATH,
    RESEND_EMAIL_PATH,
    RESERVED_TICKETS_CONFIRMATION_PATH,
    RESERVED_TICKETS_PATH,
    SERIES_PATH,
    STATUS_PATH,
    TICKETS_WITH_CAPACITY_WAITLIST_PATH,
    TICKETS_WITH_TICKET_RESELLER_PATH,
    TICKETS_FULL_SIZE_PATH,
    TICKETS_COMPACT_SIZE_PATH,
    UNLOCK_TICKETS_PATH,
    WAITLIST_CONFIRMATION_PATH,
    WAITLIST_PATH,
    WAITING_ROOM_PATH,
    PRE_ORDER_CONFIRMATION_ORGANIZER_QUESTIONS_PATH,
    PRE_TICKETS_ORGANIZER_QUESTIONS_PATH,
    ORDER_SUCCESS_ANIMATION_PATH,
    CAPTCHA_ERROR_PATH,
} from './constants';
import { TicketsAccordionLoadingSkeleton } from './components/TimedEntrySeries';
/**
 * This is a map of paths to route resolver function.
 * Paths defined on this object will be code-splitted from the main checkoutWidget bundle
 * and required on-demand when the route is requested.
 *
 * A webpackChunkName magic comment can be provided to define the file name that the code-splitted
 * bundle will have.
 */
const asyncPaths = {
    [CHECKOUT_PATH]: loadable(
        () =>
            import(
                /* webpackChunkName: 'CheckoutModule' */ './routes/ConnectedCheckoutPage'
            ),
        {
            fallback: <LoadingPage />,
        },
    ),
    [DELIVERY_METHODS_PATH]: loadable(
        () =>
            import(
                /* webpackChunkName: 'DeliveryMethodForm' */ './routes/ConnectedDeliveryMethodForm'
            ),
        {
            fallback: <LoadingPage />,
        },
    ),
    [CAPTCHA_ERROR_PATH]: loadable(
        () =>
            import(
                /* webpackChunkName: 'CaptchaErrorPageModule' */ './routes/ConnectedCaptchaErrorPage'
            ),
        {
            fallback: <LoadingPage />,
        },
    ),
    [EXISTING_ORDER_PATH]: loadable(
        () =>
            import(
                /* webpackChunkName: 'ExistingOrderModule' */ './routes/ConnectedExistingOrderPage'
            ),
        {
            fallback: <LoadingPage />,
        },
    ),
    [EXTERNAL_PAYMENT_PATH]: loadable(
        () =>
            import(
                /* webpackChunkName: 'ExternalPaymentModule' */ './routes/ConnectedExternalPaymentPage'
            ),
        {
            fallback: <LoadingPage />,
        },
    ),
    [GROUP_REG_CREATE_PATH]: loadable(
        () =>
            import(
                /* webpackChunkName: 'GroupRegCreateModule' */ './routes/ConnectedGroupRegCreatePage'
            ),
        {
            fallback: <LoadingPage />,
        },
    ),
    [GROUP_REG_JOIN_PATH]: loadable(
        () =>
            import(
                /* webpackChunkName: 'GroupRegJoinModule' */ './routes/ConnectedGroupRegJoinPage'
            ),
        {
            fallback: <LoadingPage />,
        },
    ),
    [GROUP_REG_TYPE_SELECTION_PATH]: loadable(
        () =>
            import(
                /* webpackChunkName: 'GroupRegTypeSelectionModule' */ './routes/ConnectedGroupRegTypeSelectionPage'
            ),
        {
            fallback: <LoadingPage />,
        },
    ),
    [LOGIN_PATH]: loadable(
        () =>
            import(
                /* webpackChunkName: 'LoginModule' */ './routes/ConnectedLoginPage'
            ),
        {
            fallback: <LoadingPage />,
        },
    ),
    [ORDER_SUCCESS_ANIMATION_PATH]: loadable(
        () =>
            import(
                /* webpackChunkName: 'OrderSuccessAnimationModule' */ './routes/ConnectedOrderSuccessAnimationPage'
            ),
        {
            fallback: <LoadingPage />,
        },
    ),
    [PAY_IN_PATH]: loadable(
        () =>
            import(
                /* webpackChunkName: 'PayInModule' */ './routes/ConnectedPayInPage'
            ),
        {
            fallback: <LoadingPage />,
        },
    ),
    [PAY_IN_CONFIRMATION_PATH]: loadable(
        () =>
            import(
                /* webpackChunkName: 'PayInConfirmationModule' */ './routes/ConnectedPayInConfirmationPage'
            ),
        {
            fallback: <LoadingPage />,
        },
    ),

    [PICK_A_SEAT_PATH]: loadable(
        () =>
            import(
                /* webpackChunkName: 'PickASeatModule' */ './routes/ConnectedPickASeatPage'
            ),
        {
            fallback: <LoadingPage />,
        },
    ),
    [PRE_ORDER_CONFIRMATION_ORGANIZER_QUESTIONS_PATH]: loadable(
        () =>
            import(
                /* webpackChunkName: 'dPreOrderConfirmationOrganizerQuestionsModule' */ './routes/ConnectedPreOrderConfirmationOrganizerQuestionsPage'
            ),
        {
            fallback: <LoadingPage />,
        },
    ),
    [PRE_TICKETS_ORGANIZER_QUESTIONS_PATH]: loadable(
        () =>
            import(
                /* webpackChunkName: 'PreTicketsOrganizerQuestionsModule' */ './routes/ConnectedPreTicketsOrganizerQuestionsPage'
            ),
        {
            fallback: <LoadingPage />,
        },
    ),
    [PROTECTED_PATH]: loadable(
        () =>
            import(
                /* webpackChunkName: 'ProtectedEventModule' */ './routes/ConnectedProtectedEventPage'
            ),
        {
            fallback: <LoadingPage />,
        },
    ),
    [PURCHASE_CONFIRMATION_PATH]: loadable(
        () =>
            import(
                /* webpackChunkName: 'PurchaseConfirmationModule' */ './routes/ConnectedPurchaseConfirmationPage'
            ),
        {
            fallback: <LoadingPage />,
        },
    ),
    [RESEND_EMAIL_PATH]: loadable(
        () =>
            import(
                /* webpackChunkName: 'ResendEmailForm' */ './routes/ConnectedResendEmailForm'
            ),
        {
            fallback: <LoadingPage />,
        },
    ),
    [RESERVED_TICKETS_CONFIRMATION_PATH]: loadable(
        () =>
            import(
                /* webpackChunkName: 'ReservedTicketsConfirmationModule' */ './routes/ConnectedReservedTicketsConfirmationPage'
            ),
        {
            fallback: <LoadingPage />,
        },
    ),
    [RESERVED_TICKETS_PATH]: loadable(
        () =>
            import(
                /* webpackChunkName: 'ReservedTicketSelectionModule' */ './routes/ConnectedReservedTicketSelectionPage'
            ),
        {
            fallback: <LoadingPage />,
        },
    ),
    [TICKETS_WITH_CAPACITY_WAITLIST_PATH]: loadable(
        () =>
            import(
                /* webpackChunkName: 'EventCapacityWaitlistTicketsModule' */ './routes/ConnectedEventCapacityWaitlistTicketsPage'
            ),
        {
            fallback: <LoadingPage />,
        },
    ),
    [TICKETS_WITH_TICKET_RESELLER_PATH]: loadable(() =>
        import(
            /* webpackChunkName: 'EventTicketResellerModule' */ './routes/ConnectedEventTicketResellerPage'
        ),
    ),
    [UNLOCK_TICKETS_PATH]: loadable(
        () =>
            import(
                /* webpackChunkName: 'UnlockTicketsModule' */ './routes/ConnectedUnlockTicketsPage'
            ),
        {
            fallback: <LoadingPage />,
        },
    ),
    [WAITLIST_PATH]: loadable(
        () =>
            import(
                /* webpackChunkName: 'WaitlistModule' */ './routes/ConnectedWaitlistPage'
            ),
        {
            fallback: <LoadingPage />,
        },
    ),
    [WAITLIST_CONFIRMATION_PATH]: loadable(
        () =>
            import(
                /* webpackChunkName: 'ConnnectedWaitlistConfirmationModule' */ './routes/ConnnectedWaitlistConfirmationPage'
            ),
        {
            fallback: <LoadingPage />,
        },
    ),
};

const getRoutes = () => {
    const ConnectedPage = connectPage(Page);

    /**
     * Routes defined directly under the main Route will be bundled directly on the checkoutWidget
     * main bundle.
     * Routes declared as asyncPaths will each have it's own bundle that will be loaded on demand.
     * This is done at build time by webpack.
     *
     * For better js load performance most routes should be code-splitted.
     * The most common criteria to NOT code-split a path is that such path is
     * VERY likely to be the landing route for MOST events.
     */
    return (
        <Route path={BASE_URL} component={ConnectedPage}>
            <IndexRoute component={LoadingPage} />
            <Route
                path={WAITING_ROOM_PATH}
                component={loadable(
                    () =>
                        import(
                            /* webpackChunkName: 'WaitingRoomModule' */ './routes/ConnectedWaitingRoomPage'
                        ),
                    {
                        fallback: <LoadingPage />,
                    },
                )}
            />
            <Route
                path={TICKETS_FULL_SIZE_PATH}
                component={loadable(
                    () =>
                        import(
                            /* webpackChunkName: 'TicketSelectionFullSizeModule' */ './routes/ConnectedTicketSelectionFullSizePage'
                        ),
                    {
                        fallback: <LoadingPage />,
                    },
                )}
            />
            <Route
                path={TICKETS_COMPACT_SIZE_PATH}
                component={loadable(
                    () =>
                        import(
                            /* webpackChunkName: 'TicketSelectionCompactSizeModule' */ './routes/ConnectedTicketSelectionCompactSizePage'
                        ),
                    {
                        fallback: <LoadingPage />,
                    },
                )}
            />
            <Route
                path={ONLY_CHECKOUT_PATH}
                component={loadable(
                    () =>
                        import(
                            /* webpackChunkName: 'OnlyCheckoutModule' */ './routes/ConnectedOnlyCheckoutPage'
                        ),
                    {
                        fallback: <LoadingPage />,
                    },
                )}
            />
            <Route
                path={`${STATUS_PATH}/:statusType`}
                component={loadable(
                    () =>
                        import(
                            /* webpackChunkName: 'StatusModule' */ './routes/ConnectedStatusPage'
                        ),
                    {
                        fallback: <LoadingPage />,
                    },
                )}
            />
            <Route
                path={SERIES_PATH}
                component={loadable(
                    () =>
                        import(
                            /* webpackChunkName: 'SeriesModule' */ './routes/ConnectedSeriesPage'
                        ),
                    {
                        fallback: <LoadingPage />,
                    },
                )}
            >
                <Route
                    path={TICKETS_FULL_SIZE_PATH}
                    component={loadable(
                        () =>
                            import(
                                /* webpackChunkName: 'TicketSelectionFullSizeModule' */ './routes/ConnectedTicketSelectionFullSizePage'
                            ),
                        {
                            fallback: <TicketsAccordionLoadingSkeleton />,
                        },
                    )}
                />
                <Route
                    path={TICKETS_WITH_CAPACITY_WAITLIST_PATH}
                    component={loadable(
                        () =>
                            import(
                                /* webpackChunkName: 'EventCapacityWaitlistTicketsModule' */ './routes/ConnectedEventCapacityWaitlistTicketsPage'
                            ),
                        {
                            fallback: <TicketsAccordionLoadingSkeleton />,
                        },
                    )}
                />
                <Route
                    path={`${STATUS_PATH}/:statusType`}
                    component={loadable(
                        () =>
                            import(
                                /* webpackChunkName: 'StatusModule' */ './routes/ConnectedStatusPage'
                            ),
                        {
                            fallback: <TicketsAccordionLoadingSkeleton />,
                        },
                    )}
                />
            </Route>
            {Object.entries(asyncPaths).map(([path, component]) => (
                <Route key={path} path={path} component={component} />
            ))}
        </Route>
    );
};

export default getRoutes;
